import {
    type AppOptionsType,
    type MapContextType,
    type PointToDrawType,
    type RouteToDrawType,
} from '../EasyVrpApp.types';
import type { Vehicle } from '../../Vehicles/Vehicle';

export class EasyVrpMapContext {
    private context: MapContextType;

    constructor(
        selectedCalcId: number,
        routesToDraw: RouteToDrawType[],
        vehicles: Vehicle[],
        extra_data: AppOptionsType
    ) {
        // if uniqueRouteId is missing in any route, use vehicleId instead as string - for backward compatibility
        routesToDraw.forEach(route => {
            if (
                (route.uniqueRouteId === null || route.uniqueRouteId == '') &&
                route.vehicleId !== null
            ) {
                route.uniqueRouteId = `vehicle_${route.vehicleId}`;
            }
        });
        try {
            this.context = {
                selectedVehicleId: null, // Initially no vehicle is selected
                selectedRouteId: null, // Initially no route is selected
                selectedCalcId,
                routesToDraw,
                vehicles,
                extra_data,
            };
        } catch (error) {
            console.error('#map Error parsing input or output:', error);
            throw new Error('Invalid input or output format for EasyVrpMapContext');
        }
    }

    public getSelectedCalcId(): number | null {
        return this.context.selectedCalcId;
    }

    public setSelectedRouteId(routeId: string | null): this {
        this.context.selectedRouteId = routeId;
        return this;
    }

    public getSelectedRouteId(): string | null {
        return this.context.selectedRouteId;
    }

    public setContext(context: MapContextType): void {
        this.context = context;
    }

    public getContext(): MapContextType {
        return this.context;
    }

    public getRouteIds(): string[] {
        return this.context.routesToDraw
            .filter(route => route.uniqueRouteId !== null)
            .map(route => route.uniqueRouteId as string);
    }

    public getRoutesWithoutVehicle(): RouteToDrawType[] {
        return this.context.routesToDraw
            .filter(r => r.vehicleId === null)
            .map(r => {
                return {
                    uniqueRouteId: r.uniqueRouteId,
                    vehicleId: r.vehicleId,
                    color: r.color,
                    route: r.route,
                    points: r.points,
                    name: r.name || 'missing-name',
                    summary: r.summary || null,
                };
            });
    }

    public getPointsByRouteId(selectedRouteId: string): PointToDrawType[] {
        let routeToDraw = this.context.routesToDraw.find(
            route => route.uniqueRouteId === selectedRouteId
        );
        if (!routeToDraw) {
            console.warn(`#map No route found for ID ${selectedRouteId}`);
            return [];
        }
        return routeToDraw.points;
    }

    public getPointsWithoutVehicle(): PointToDrawType[] {
        return this.context.routesToDraw
            .filter(route => route.vehicleId === null)
            .flatMap(route => route.points);
    }

    public getColor(routeId: string): string | null {
        let routeToDraw = this.context.routesToDraw.find(route => route.uniqueRouteId === routeId);
        if (!routeToDraw) {
            console.warn(`#map No route found for vehicle ID ${routeId}`);
            return null;
        }
        return routeToDraw.color;
    }

    public getVehicleNameFromRoutesToDraw(vehicleId: number): string | null {
        let routeToDraw = this.context.routesToDraw.find(route => route.vehicleId === vehicleId);
        if (!routeToDraw) {
            console.warn(
                `#map No route found for vehicle ID ${vehicleId}`,
                this.context.routesToDraw
            );
            return null;
        }
        return routeToDraw.name;
    }

    public getVehicleNameFromVehicles(vehicleId: number): string | null {
        let vehicle = this.context.vehicles.find(v => v.getId() === vehicleId);
        if (!vehicle) {
            console.warn(`#map No vehicle found for ID ${vehicleId}`);
            return null;
        }
        return vehicle.getName();
    }

    public getVehicleName(vehicleId: number): string | null {
        let name: string | null = null;
        if (!name) {
            name = this.getVehicleNameFromRoutesToDraw(vehicleId);
        }
        if (!name) {
            name = this.getVehicleNameFromVehicles(vehicleId);
        }
        return name;
    }

    public getRouteByRouteId(
        routeId: string,
        useStartPoint: boolean,
        useEndPoint: boolean
    ): { x: number; y: number }[] {
        let routes: { x: number; y: number }[] = [];
        let routeToDraw =
            this.context.routesToDraw.find(route => route.uniqueRouteId === routeId) ?? null;
        if (!routeToDraw) {
            console.warn(`#map No route found for routeId ${routeId}`);
            return [];
        }
        routeToDraw.route.forEach(coords => {
            routes.push({
                x: coords[0],
                y: coords[1],
            });
        });
        return routes;
    }

    getUnassignedJobIds(): number[] {
        return []; // This method is not implemented in the original code, so returning an empty array
    }

    getStartPoint(routeId: string): { x: number; y: number } | null {
        let coords =
            this.context.routesToDraw.find(route => route.uniqueRouteId === routeId)?.route[0] ??
            undefined;
        if (coords && coords.length === 2) {
            return {
                x: coords[0],
                y: coords[1],
            };
        }
        return null;
    }

    getEndPoint(routeId: string): { x: number; y: number } | null {
        let coords =
            this.context.routesToDraw
                .find(route => route.uniqueRouteId === routeId)
                ?.route.slice(-1)[0] ?? undefined;
        if (coords && coords.length === 2) {
            return {
                x: coords[0],
                y: coords[1],
            };
        }
        return null;
    }
}
